import { create } from 'zustand';

import { getActiveProjectId } from '../../engine/editor/persistence/ProjectPersistence';
import { isValidScriptName, uniqueScriptName } from '../../engine/scripting/scriptModules';
import { deleteScript as deleteScriptRequest, listScripts, saveScript as saveScriptRequest, type ScriptDocument } from '../services/scripts';

// The Code workspace's script roster: every script in the project, which
// ones are open as editor tabs, which one is active, and what has and has
// not been saved yet. Mirrors gunsmith/store/gunsmithStore.ts's shape (a
// roster of independently named/saved/deleted documents, see migration
// 0028_script_documents.sql) with one deliberate difference from the first
// version of this store: saving is automatic. Every other authoring
// surface in the editor debounce-saves as the creator works
// (`useModeDocument`, `useSharedModeDocument`, the prefab library), and a
// code editor that silently kept unsaved work behind a Save button was the
// one place a creator could lose a file by navigating away. Edits now save
// themselves `AUTOSAVE_DELAY_MS` after the last keystroke; Ctrl/Cmd+S saves
// immediately; the editor's status bar says which state a file is in.
//
// Undo/redo lives in Monaco's own per-file models (HollowScriptFileEditor),
// not here - a second, competing app-level history for the same edits would
// only ever disagree with the first.

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DELAY_MS = 1200;

export type RenameResult = { readonly ok: true } | { readonly ok: false; readonly error: string };

export interface CreateScriptOptions {
  readonly source?: string;
  readonly runOnStart?: boolean;
}

interface ScriptState {
  scripts: ScriptDocument[];
  sourceScripts: ScriptDocument[];
  activeScriptId: string | null;
  /** Open editor tabs, in tab order. Always contains `activeScriptId` when
   * that is non-null. A script can be in the roster without being open. */
  openScriptIds: string[];
  rosterLoaded: boolean;
  /** Project id the currently-loaded roster belongs to, so a project switch
   * (client-side navigation, no reload) can tell a stale `rosterLoaded` left
   * over from the previous project apart from a genuinely current roster. */
  rosterProjectId: string | null;
  /** Scripts with a save request in flight right now. */
  savingIds: ReadonlySet<string>;

  saveState: SaveState;
  statusMessage: string | null;
  /** Epoch ms of the last successful save, for the status bar's "Saved". */
  lastSavedAt: number | null;

  hydrateFromRoster: (scripts: ScriptDocument[]) => void;
  loadRoster: () => Promise<void>;

  createScript: (name: string, options?: CreateScriptOptions) => string;
  /** Validated rename: rejects an empty/unusable name (see `isValidScriptName`)
   * or one another script already has, and says why. */
  renameScript: (scriptId: string, name: string) => RenameResult;
  renameActiveScript: (name: string) => void;
  setActiveSource: (source: string) => void;
  setActiveRunOnStart: (runOnStart: boolean) => void;
  setRunOnStart: (scriptId: string, runOnStart: boolean) => void;
  /** Makes `id` active, opening it as a tab if it was not one already. */
  setActiveScriptId: (id: string | null) => void;
  openScript: (id: string) => void;
  /** Closes a tab. Never discards work: a pending autosave for it is flushed
   * first, so closing an edited file saves it rather than losing the edit. */
  closeScript: (id: string) => void;
  /** Copies a script (as `<name>_2`, `<name>_3`, ...) and opens the copy. */
  duplicateScript: (scriptId: string) => string | null;
  /** A script never saved (absent from sourceScripts) is only ever local
   * state and is just dropped. A saved script is removed from the working
   * roster optimistically, then deleted server-side; a failure restores it
   * rather than leaving the UI showing a script as gone when it still exists
   * on the server - mirrors gunsmithStore's deleteWeapon exactly. */
  deleteScript: (scriptId: string) => Promise<void>;

  saveScript: (scriptId: string) => Promise<void>;
  saveActiveScript: () => Promise<void>;
  saveAll: () => Promise<void>;
  /** Sends every pending autosave right now (page hide, leaving the
   * workspace). */
  flushPendingSaves: () => Promise<void>;
  resetActiveToSource: () => void;
  clearStatus: () => void;
}

export function scriptsEqual(a: ScriptDocument, b: ScriptDocument): boolean {
  return a.id === b.id && a.name === b.name && a.source === b.source && a.runOnStart === b.runOnStart;
}

/** Whether the roster differs from what was last loaded/saved - a script
 * added or removed counts as dirty too, not just an edited field. */
export function isScriptRosterDirty(scripts: ScriptDocument[], sourceScripts: ScriptDocument[]): boolean {
  if (scripts.length !== sourceScripts.length) return true;
  const sourceById = new Map(sourceScripts.map((s) => [s.id, s]));
  return scripts.some((script) => {
    const source = sourceById.get(script.id);
    return !source || !scriptsEqual(script, source);
  });
}

/** Whether one script has edits not yet saved. */
export function isScriptDirty(state: Pick<ScriptState, 'scripts' | 'sourceScripts'>, scriptId: string): boolean {
  const script = state.scripts.find((s) => s.id === scriptId);
  if (!script) return false;
  const source = state.sourceScripts.find((s) => s.id === scriptId);
  return !source || !scriptsEqual(script, source);
}

/** Every script with unsaved edits, in roster order. */
export function dirtyScriptIds(state: Pick<ScriptState, 'scripts' | 'sourceScripts'>): string[] {
  return state.scripts.filter((s) => isScriptDirty(state, s.id)).map((s) => s.id);
}

function makeScriptId(): string {
  return `script_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Whether `projectId` is no longer the active project - a request for it is
 * stale and its result should be dropped. Mirrors gunsmithStore's own
 * isStaleProject. */
function isStaleProject(projectId: string): boolean {
  try {
    return getActiveProjectId() !== projectId;
  } catch {
    return true;
  }
}

// Per-script autosave timers. Module-level rather than store state: a timer
// handle is not something to render or persist, and keeping it out of the
// store means scheduling one never triggers a re-render of every subscriber.
const autosaveTimers = new Map<string, ReturnType<typeof setTimeout>>();

function cancelAutosave(scriptId: string): void {
  const timer = autosaveTimers.get(scriptId);
  if (timer !== undefined) {
    clearTimeout(timer);
    autosaveTimers.delete(scriptId);
  }
}

function withoutId(ids: readonly string[], id: string): string[] {
  return ids.filter((x) => x !== id);
}

function updateScript(
  get: () => ScriptState,
  set: (patch: Partial<ScriptState>) => void,
  scriptId: string,
  updater: (script: ScriptDocument) => ScriptDocument,
): boolean {
  const state = get();
  if (!state.scripts.some((s) => s.id === scriptId)) return false;
  set({
    scripts: state.scripts.map((s) => (s.id === scriptId ? updater({ ...s }) : s)),
    saveState: 'idle',
  });
  return true;
}

export const useScriptStore = create<ScriptState>((set, get) => {
  function scheduleAutosave(scriptId: string): void {
    cancelAutosave(scriptId);
    autosaveTimers.set(
      scriptId,
      setTimeout(() => {
        autosaveTimers.delete(scriptId);
        void get().saveScript(scriptId);
      }, AUTOSAVE_DELAY_MS),
    );
  }

  return {
    scripts: [],
    sourceScripts: [],
    activeScriptId: null,
    openScriptIds: [],
    rosterLoaded: false,
    rosterProjectId: null,
    savingIds: new Set(),

    saveState: 'idle',
    statusMessage: null,
    lastSavedAt: null,

    hydrateFromRoster: (scripts) => {
      const state = get();
      const ids = new Set(scripts.map((s) => s.id));
      // A reload keeps the creator's place when the same scripts are still
      // there (a Preview round trip, a reconnect), rather than always
      // snapping back to the first file.
      const openScriptIds = state.openScriptIds.filter((id) => ids.has(id));
      const activeScriptId = state.activeScriptId && ids.has(state.activeScriptId) ? state.activeScriptId : (openScriptIds[0] ?? scripts[0]?.id ?? null);
      if (activeScriptId && !openScriptIds.includes(activeScriptId)) openScriptIds.push(activeScriptId);
      set({
        scripts: scripts.map((s) => ({ ...s })),
        sourceScripts: scripts.map((s) => ({ ...s })),
        activeScriptId,
        openScriptIds,
        rosterLoaded: true,
        saveState: 'idle',
        statusMessage: null,
      });
    },

    loadRoster: async () => {
      let projectId: string;
      try {
        projectId = getActiveProjectId();
      } catch {
        return; // No active project (the editor was already left) - nothing to load.
      }
      // A different project's roster is never carried over, not even as
      // "the place the creator was" - its ids mean nothing here.
      if (get().rosterProjectId !== projectId) {
        for (const id of autosaveTimers.keys()) cancelAutosave(id);
        set({ scripts: [], sourceScripts: [], activeScriptId: null, openScriptIds: [], rosterLoaded: false });
      }
      set({ saveState: 'saving', statusMessage: 'Loading your scripts...' });
      try {
        const scripts = await listScripts(projectId);
        if (isStaleProject(projectId)) return;
        get().hydrateFromRoster(scripts);
        set({ saveState: 'idle', statusMessage: null, rosterProjectId: projectId });
      } catch (error) {
        if (isStaleProject(projectId)) return;
        set({
          saveState: 'error',
          statusMessage: error instanceof Error ? error.message : 'Failed to load your scripts.',
        });
      }
    },

    createScript: (name, options = {}) => {
      const id = makeScriptId();
      const script: ScriptDocument = { id, name, source: options.source ?? '', runOnStart: options.runOnStart ?? false };
      set((state) => ({
        scripts: [...state.scripts, script],
        activeScriptId: id,
        openScriptIds: [...state.openScriptIds, id],
        statusMessage: `Created "${name}".`,
        saveState: 'idle',
      }));
      scheduleAutosave(id);
      return id;
    },

    renameScript: (scriptId, name) => {
      const state = get();
      const script = state.scripts.find((s) => s.id === scriptId);
      if (!script) return { ok: false, error: 'That script no longer exists.' };
      if (name === script.name) return { ok: true };
      if (!isValidScriptName(name)) {
        return { ok: false, error: 'Use a plain name with no slashes, dots or quotes - other scripts import it as "./<name>".' };
      }
      if (state.scripts.some((s) => s.id !== scriptId && s.name === name)) {
        return { ok: false, error: `Another script is already called "${name}".` };
      }
      updateScript(get, set, scriptId, (s) => ({ ...s, name }));
      scheduleAutosave(scriptId);
      return { ok: true };
    },

    renameActiveScript: (name) => {
      const id = get().activeScriptId;
      if (id) get().renameScript(id, name);
    },

    setActiveSource: (source) => {
      const id = get().activeScriptId;
      if (!id) return;
      if (updateScript(get, set, id, (s) => ({ ...s, source }))) scheduleAutosave(id);
    },

    setActiveRunOnStart: (runOnStart) => {
      const id = get().activeScriptId;
      if (id) get().setRunOnStart(id, runOnStart);
    },

    setRunOnStart: (scriptId, runOnStart) => {
      if (updateScript(get, set, scriptId, (s) => ({ ...s, runOnStart }))) scheduleAutosave(scriptId);
    },

    setActiveScriptId: (id) => {
      if (id === null) {
        set({ activeScriptId: null });
        return;
      }
      get().openScript(id);
    },

    openScript: (id) => {
      const state = get();
      if (!state.scripts.some((s) => s.id === id)) return;
      set({
        activeScriptId: id,
        openScriptIds: state.openScriptIds.includes(id) ? state.openScriptIds : [...state.openScriptIds, id],
      });
    },

    closeScript: (id) => {
      const state = get();
      if (!state.openScriptIds.includes(id)) return;
      if (autosaveTimers.has(id)) {
        cancelAutosave(id);
        void get().saveScript(id);
      }
      const index = state.openScriptIds.indexOf(id);
      const openScriptIds = withoutId(state.openScriptIds, id).filter((openId) => state.scripts.some((s) => s.id === openId));
      // The neighbour to the right takes over, else the one to the left -
      // the tab-close convention every browser and IDE shares.
      const nextActive = state.activeScriptId === id ? (openScriptIds[index] ?? openScriptIds[index - 1] ?? null) : state.activeScriptId;
      set({ openScriptIds, activeScriptId: nextActive });
    },

    duplicateScript: (scriptId) => {
      const state = get();
      const original = state.scripts.find((s) => s.id === scriptId);
      if (!original) return null;
      const name = uniqueScriptName(original.name, new Set(state.scripts.map((s) => s.name)));
      return get().createScript(name, { source: original.source, runOnStart: false });
    },

    deleteScript: async (scriptId) => {
      cancelAutosave(scriptId);
      const state = get();
      const sourceScript = state.sourceScripts.find((s) => s.id === scriptId);
      const wasActive = state.activeScriptId === scriptId;

      const remainingScripts = state.scripts.filter((s) => s.id !== scriptId);
      const openIndex = state.openScriptIds.indexOf(scriptId);
      // Only ids that still name a script can take over as the active tab -
      // never a stale id left in the open list by an earlier roster.
      const remainingOpen = withoutId(state.openScriptIds, scriptId).filter((id) => remainingScripts.some((s) => s.id === id));
      const nextActiveScriptId = wasActive
        ? (remainingOpen[openIndex] ?? remainingOpen[openIndex - 1] ?? remainingScripts[0]?.id ?? null)
        : state.activeScriptId;
      const openScriptIds = nextActiveScriptId && !remainingOpen.includes(nextActiveScriptId) ? [...remainingOpen, nextActiveScriptId] : remainingOpen;

      if (!sourceScript) {
        set({
          scripts: remainingScripts,
          activeScriptId: nextActiveScriptId,
          openScriptIds,
          statusMessage: 'Removed script.',
          saveState: 'idle',
        });
        return;
      }

      const workingScript = state.scripts.find((s) => s.id === scriptId) ?? sourceScript;
      set({
        scripts: remainingScripts,
        sourceScripts: state.sourceScripts.filter((s) => s.id !== scriptId),
        activeScriptId: nextActiveScriptId,
        openScriptIds,
        saveState: 'saving',
        statusMessage: 'Deleting...',
      });

      try {
        await deleteScriptRequest(getActiveProjectId(), scriptId);
        set({ saveState: 'idle', statusMessage: `Deleted "${sourceScript.name}".` });
      } catch (error) {
        set((s) => ({
          scripts: [...s.scripts, { ...workingScript }],
          sourceScripts: [...s.sourceScripts, { ...sourceScript }],
          // Only reclaim focus onto the restored script if it was the one
          // active *before* this delete (and the optimistic switch hasn't
          // since been overtaken by the creator manually picking something
          // else).
          activeScriptId: wasActive && s.activeScriptId === nextActiveScriptId ? scriptId : s.activeScriptId,
          openScriptIds: s.openScriptIds.includes(scriptId) ? s.openScriptIds : [...s.openScriptIds, scriptId],
          saveState: 'error',
          statusMessage: error instanceof Error ? error.message : 'Failed to delete.',
        }));
      }
    },

    saveScript: async (scriptId) => {
      cancelAutosave(scriptId);
      const state = get();
      const script = state.scripts.find((s) => s.id === scriptId);
      if (!script) return;
      if (!isScriptDirty(state, scriptId) || state.savingIds.has(scriptId)) return;
      let projectId: string;
      try {
        projectId = getActiveProjectId();
      } catch {
        return;
      }
      const snapshot = { ...script };
      set((s) => ({ savingIds: new Set([...s.savingIds, scriptId]), saveState: 'saving', statusMessage: 'Saving...' }));
      try {
        const saved = await saveScriptRequest(projectId, snapshot);
        if (isStaleProject(projectId)) return;
        set((s) => {
          const savingIds = new Set(s.savingIds);
          savingIds.delete(scriptId);
          return {
            sourceScripts: [...s.sourceScripts.filter((x) => x.id !== saved.id), { ...saved }],
            savingIds,
            saveState: savingIds.size > 0 ? 'saving' : 'saved',
            statusMessage: `Saved "${saved.name}".`,
            lastSavedAt: Date.now(),
          };
        });
        // Edits that landed while the request was in flight are still
        // unsaved - the snapshot above is what the server has - so queue
        // another save for them rather than leaving them dirty forever.
        if (isScriptDirty(get(), scriptId)) scheduleAutosave(scriptId);
      } catch (error) {
        if (isStaleProject(projectId)) return;
        set((s) => {
          const savingIds = new Set(s.savingIds);
          savingIds.delete(scriptId);
          return {
            savingIds,
            saveState: 'error',
            statusMessage: error instanceof Error ? error.message : 'Failed to save.',
          };
        });
      }
    },

    saveActiveScript: async () => {
      const id = get().activeScriptId;
      if (id) await get().saveScript(id);
    },

    saveAll: async () => {
      await Promise.all(dirtyScriptIds(get()).map((id) => get().saveScript(id)));
    },

    flushPendingSaves: async () => {
      const pending = [...autosaveTimers.keys()];
      for (const id of pending) cancelAutosave(id);
      await Promise.all(pending.map((id) => get().saveScript(id)));
    },

    resetActiveToSource: () => {
      const state = get();
      const source = state.sourceScripts.find((s) => s.id === state.activeScriptId);
      if (!source) return;
      cancelAutosave(source.id);
      set({
        scripts: state.scripts.map((s) => (s.id === source.id ? { ...source } : s)),
        statusMessage: 'Reverted unsaved changes.',
        saveState: 'idle',
      });
    },

    clearStatus: () => set({ statusMessage: null }),
  };
});
